import { useCallback, useEffect, useState } from 'react';
import { BadgeCheck } from 'lucide-react';
import api from '@/lib/api';
import MusicCardCompact from '@/components/MusicCardCompact';
import MusicCardDetail from '@/components/MusicCardDetail';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

export default function Owned() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/cards', { params: { status: 'owned' } });
      setCards(data.cards || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleUnown = async (card) => {
    try {
      await api.post(`/cards/${card.id}/unown`);
      toast.success('Moved back to your collection');
      setSelected(null);
      fetch();
    } catch (e) {
      toast.error('Failed');
    }
  };
  const handleDelete = async (card) => {
    if (!window.confirm('Delete this card?')) return;
    try {
      await api.delete(`/cards/${card.id}`);
      toast.success('Deleted');
      setSelected(null);
      fetch();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const handleToggleWant = async (card) => {
    const prevWant = !!card.want;
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, want: !prevWant } : c)));
    setSelected((sel) => (sel && sel.id === card.id ? { ...sel, want: !prevWant } : sel));
    try {
      const { data } = await api.post(`/cards/${card.id}/toggle-want`);
      toast.success(data?.want ? 'Added to want list' : 'Removed from want list');
      setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, want: !!data?.want } : c)));
      setSelected((sel) => (sel && sel.id === card.id ? { ...sel, want: !!data?.want } : sel));
    } catch (e) {
      setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, want: prevWant } : c)));
      setSelected((sel) => (sel && sel.id === card.id ? { ...sel, want: prevWant } : sel));
      toast.error(e?.response?.data?.detail || 'Failed to update want list');
    }
  };

  const handleGenreChange = async (card, newBucket) => {
    try {
      const { data } = await api.patch(`/cards/${card.id}/genre`, { genre_bucket: newBucket });
      toast.success(`Moved to ${newBucket} crate`);
      setSelected(data || { ...card, genre_bucket: newBucket });
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change genre');
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Owned
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tracks you&rsquo;ve bought or already have.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={`skeleton-${i}`} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="owned-empty">
          <BadgeCheck className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          <h3 className="font-display text-lg font-semibold">Nothing marked Owned yet</h3>
          <p className="text-sm text-muted-foreground">When you buy a track, mark it Owned to keep your collection clean.</p>
        </div>
      ) : (
        <ul className="space-y-2 max-w-4xl mx-auto" data-testid="owned-cards-list">
          {cards.map((card) => (
            <li key={card.id}>
              <MusicCardCompact
                card={card}
                onOpen={setSelected}
                onUnown={handleUnown}
                onDelete={handleDelete}
                showOwnedActions
                isPreviewing={previewingId === card.id}
                onPreviewToggle={() => setPreviewingId((id) => (id === card.id ? null : card.id))}
              />
            </li>
          ))}
        </ul>
      )}

      <MusicCardDetail
        card={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onUnown={handleUnown}
        onDelete={handleDelete}
        onGenreChange={handleGenreChange}
        onToggleWant={handleToggleWant}
        deck={cards}
        deckIndex={selected ? cards.findIndex((c) => c.id === selected.id) : -1}
        onNavigate={(newCard) => setSelected(newCard)}
      />
    </div>
  );
}
